import React from 'react';
import BaseEChart from './BaseEChart';
import { CHART_COLORS, CHART_FONT_FAMILY } from './chartConstants';
import type { ChartOption } from '@app-types/chart';
import type { RadarDimension, ScoreValue } from '@app-types/performance';
import { formatNumber } from '@utils/format';

export interface ScoreRadarChartProps {
  dimensions: RadarDimension[];
  axisMax: number;
  height?: number;
}

const COMPANY_NAME = '対象銘柄';
const INDUSTRY_NAME = '業界平均';

// ECharts lays radar indicators out counter-clockwise after the top point.
// Keep this translation chart-local so the Enterprise Score table retains its
// API/Figma domain order.
export const SCORE_RADAR_CHART_DIMENSION_ORDER = [
  'profitability',
  'valuation',
  'safety',
  'soundness',
  'momentum',
] as const;

const RADAR_LABEL_LAYOUT_BY_KEY: Record<string, {
  text: string;
  x: number;
  y: number;
}> = {
  // Figma node 3484:25183 positions each label from the radar-area center.
  profitability: { text: '収益性', x: -2.48, y: -76.5 },
  momentum: { text: '株価\nモメンタム', x: 95.5, y: -11.95 },
  soundness: { text: '財務健全性', x: 69, y: 65.05 },
  safety: { text: '安全性', x: -62.54, y: 64.05 },
  valuation: { text: '割安性', x: -99, y: -16.9 },
};

export interface ScoreRadarChartModel {
  dimensions: RadarDimension[];
  indicatorNames: string[];
  companyValues: ScoreValue[];
  industryValues: ScoreValue[];
}

const toChartValue = (value: ScoreValue | undefined): ScoreValue =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

interface RadarTooltipParam {
  /** Data-item name: `対象銘柄` or `業界平均`. */
  name?: string;
  /** Internal ECharts series name; not user-facing tooltip copy. */
  seriesName?: string;
  value?: unknown;
  color?: string;
}

const escapeHtml = (value: string): string =>
  value.replace(/[&<>"']/g, (char) => {
    switch (char) {
      case '&': return '&amp;';
      case '<': return '&lt;';
      case '>': return '&gt;';
      case '"': return '&quot;';
      default: return '&#39;';
    }
  });

const tooltipRow = (name: string, value: string, color?: string): string => {
  const marker = color
    ? `<span class="esmart-chart-tooltip__marker" style="background:${escapeHtml(color)}"></span>`
    : '';
  return `<div class="esmart-chart-tooltip__row">${marker}<span class="esmart-chart-tooltip__name">${escapeHtml(
    name,
  )}</span><span class="esmart-chart-tooltip__value">${value}</span></div>`;
};

const formatTooltipScore = (value: unknown): string => {
  const score = typeof value === 'number' && Number.isFinite(value) ? value : null;
  return formatNumber(score, score !== null && Number.isInteger(score) ? 0 : 2);
};

/**
 * Builds a radar-only presentation model without mutating the domain/table
 * dimensions. Known dimensions use the Figma physical order; unexpected
 * dimensions remain available at the end rather than causing chart failure.
 */
export const buildScoreRadarChartModel = (
  dimensions: RadarDimension[],
): ScoreRadarChartModel => {
  const dimensionByKey = new Map(dimensions.map((dimension) => [dimension.key, dimension]));
  const knownKeys = new Set<string>(SCORE_RADAR_CHART_DIMENSION_ORDER);
  const orderedDimensions = [
    ...SCORE_RADAR_CHART_DIMENSION_ORDER
      .map((key) => dimensionByKey.get(key))
      .filter((dimension): dimension is RadarDimension => Boolean(dimension)),
    ...dimensions.filter((dimension) => !knownKeys.has(dimension.key)),
  ];

  return {
    dimensions: orderedDimensions,
    // Axis labels are rendered as Figma-positioned graphic text below. Empty
    // indicator names prevent ECharts from applying one global alignment rule
    // that would pull the right-side two-line label over the radar polygon.
    indicatorNames: orderedDimensions.map(() => ''),
    companyValues: orderedDimensions.map((dimension) =>
      toChartValue(dimension.chartCompanyValue ?? dimension.companyValue),
    ),
    industryValues: orderedDimensions.map((dimension) =>
      toChartValue(dimension.chartIndustryValue ?? dimension.industryValue),
    ),
  };
};

/**
 * Restores the radar tooltip's dimension titles without re-enabling ECharts'
 * auto-positioned axis labels. Tooltip rows intentionally use the incoming
 * table/domain order rather than the counter-clockwise chart render order.
 */
export const buildScoreRadarTooltipHtml = (
  params: unknown,
  tableDimensions: RadarDimension[],
  chartDimensions: RadarDimension[],
): string => {
  const param = (Array.isArray(params) ? params[0] : params) as RadarTooltipParam | undefined;
  if (!param || !Array.isArray(param.value) || param.value.length === 0) return '';
  const values = param.value;

  const chartIndexByKey = new Map(
    chartDimensions.map((dimension, index) => [dimension.key, index]),
  );
  const rows = tableDimensions.map((dimension) => {
    const valueIndex = chartIndexByKey.get(dimension.key);
    const value = valueIndex === undefined ? null : values[valueIndex];
    return tooltipRow(dimension.label, formatTooltipScore(value), param.color);
  }).join('');

  // Preserve the pre-rework tooltip title: the hovered data item name, not
  // the internal `Stock Report Plus Chart` series name.
  const titleName = param.name ?? param.seriesName;
  const title = titleName
    ? `<div class="esmart-chart-tooltip__title">${escapeHtml(titleName)}</div>`
    : '';
  return `${title}${rows}`;
};

export const buildScoreRadarChartOption = (
  dimensions: RadarDimension[],
  axisMax: number,
): ChartOption => {
  const chartModel = buildScoreRadarChartModel(dimensions);

  return {
    tooltip: {
      trigger: 'item',
      className: 'esmart-chart-tooltip esmart-chart-tooltip--score-radar',
      formatter: (params: unknown) =>
        buildScoreRadarTooltipHtml(params, dimensions, chartModel.dimensions),
      textStyle: {
        fontSize: 12,
        fontFamily: CHART_FONT_FAMILY,
      },
      position: 'top',
      renderMode: 'html',
    },
    radar: {
      shape: 'polygon',
      splitNumber: 4,
      // Figma node 3484:25183 is a 167px dedicated drawing area. Labels render
      // through the graphic layer below, so they do not compete with radar size.
      radius: '80%',
      center: ['50%', '52%'],
      indicator: chartModel.indicatorNames.map((name) => ({ name, max: axisMax })),
      splitLine: {
        lineStyle: {
          color: [
            'rgba(153, 153, 153, 0.1)',
            'rgba(153, 153, 153, 0.1)',
            'rgba(153, 153, 153, 0.1)',
            'rgba(153, 153, 153, 0.5)',
          ],
          width: 1,
        },
      },
      splitArea: { show: false },
      axisLine: {
        lineStyle: {
          color: 'rgba(153, 153, 153, 0.5)',
          width: 1,
        },
      },
    },
    graphic: {
      elements: [
        {
          type: 'group',
          left: 'center',
          top: 'middle',
          bounding: 'raw',
          z: 10,
          silent: true,
          children: chartModel.dimensions.flatMap((dimension) => {
            const layout = RADAR_LABEL_LAYOUT_BY_KEY[dimension.key];
            if (!layout) return [];

            return [{
              type: 'text' as const,
              style: {
                text: layout.text,
                x: layout.x,
                y: layout.y,
                align: 'center' as const,
                verticalAlign: 'middle' as const,
                fill: CHART_COLORS.axisLabel,
                fontFamily: CHART_FONT_FAMILY,
                fontSize: 12,
                fontWeight: 500,
                lineHeight: 14,
              },
            }];
          }),
        },
      ],
    },
    series: [
      {
        name: 'Stock Report Plus Chart',
        type: 'radar',
        symbol: 'circle',
        symbolSize: 5,
        colorBy: 'data',
        data: [
          {
            name: INDUSTRY_NAME,
            value: chartModel.industryValues,
            lineStyle: {
              color: CHART_COLORS.seriesCompare,
              width: 2,
            },
            areaStyle: {
              color: CHART_COLORS.seriesCompare,
              opacity: 0.3,
            },
            itemStyle: {
              color: CHART_COLORS.seriesCompare,
            },
            emphasis: {
              disabled: true,
            },
          },
          {
            name: COMPANY_NAME,
            value: chartModel.companyValues,
            lineStyle: {
              color: CHART_COLORS.seriesPrimary,
              width: 2,
            },
            areaStyle: {
              color: CHART_COLORS.seriesPrimary,
              opacity: 0.3,
            },
            itemStyle: {
              color: CHART_COLORS.seriesPrimary,
            },
            emphasis: {
              disabled: true,
            },
          },
        ],
      },
    ],
  };
};

/** Enterprise score radar with a Figma-aligned external legend row. */
const ScoreRadarChart: React.FC<ScoreRadarChartProps> = ({
  dimensions,
  axisMax,
  height = 167,
}) => {
  const option = buildScoreRadarChartOption(dimensions, axisMax);

  return (
    <div className="score-radar-chart">
      <BaseEChart
        option={option}
        height={height}
        className="score-radar-chart__canvas"
        ariaLabel="企業スコアのレーダーチャート（対象銘柄と業界平均の比較）"
        animate
      />
      <ul className="score-radar-chart__legend" aria-label="企業スコア比較">
        <li className="score-radar-chart__legend-item">
          <span
            className="score-radar-chart__legend-swatch"
            style={{ backgroundColor: CHART_COLORS.seriesPrimary }}
            aria-hidden="true"
          />
          <span>{COMPANY_NAME}</span>
        </li>
        <li className="score-radar-chart__legend-item">
          <span
            className="score-radar-chart__legend-swatch"
            style={{ backgroundColor: CHART_COLORS.seriesCompare }}
            aria-hidden="true"
          />
          <span>{INDUSTRY_NAME}</span>
        </li>
      </ul>
    </div>
  );
};

export default ScoreRadarChart;
